import React from 'react';
import { MagnifyingGlassIcon, MapLayerIcon } from '@rsos/assets/icons';
import {
  Dropdown,
  EmptyState,
  ResultContent,
  ResultIcon,
  ResultItem,
  ResultLabel,
  ResultSublabel,
} from '../GlobalMapSearch.styles';
import styles from './ActionPalette.module.css';
import type { ActionArgSuggestion, ActionCommandDef } from './types';

type ArgSourceFilter = 'all' | 'state' | 'global';

/**
 * Resolve a leading icon for an action command. Mapped per id so
 * future actions can pick a glyph that matches their effect; defaults
 * to a generic search-glyph for anything unrecognized.
 */
function iconForAction(id: string): React.ReactNode {
  switch (id) {
    case 'add':
      return <MapLayerIcon width={16} height={16} />;
    default:
      return <MagnifyingGlassIcon width={16} height={16} />;
  }
}

interface ActionPaletteProps {
  dropdownRef: React.RefObject<HTMLDivElement>;
  /**
   * Pre-filtered action suggestions. Used when the user is still
   * typing the action name (catalog mode).
   */
  suggestions: ActionCommandDef[];
  /**
   * Whether the user has typed only `//` so far (no action name yet).
   * Used to switch the header label between the empty-prefix catalog
   * state and the typing state.
   */
  isCatalog: boolean;
  /**
   * True when the palette should render `argSuggestions` instead of
   * `suggestions`. The host derives this from `useActionMode`.
   */
  isArgMode: boolean;
  /** Live argument autocomplete rows, surfaced while in arg mode. */
  argSuggestions: ActionArgSuggestion[];
  /** The committed action driving arg mode. */
  argActionId?: string;
  /** Source filter for mixed catalogs in arg mode. */
  argSourceFilter?: ArgSourceFilter;
  /** Source counts derived from the unfiltered arg list. */
  argSourceCounts?: {
    state: number;
    global: number;
  };
  /** True while state-layer catalog fetch is pending for current PSAP. */
  isStateCatalogLoading?: boolean;
  /** Update arg source filter. */
  onArgSourceFilterChange?: (next: ArgSourceFilter) => void;
  cursor: number;
  onCursorChange: (idx: number) => void;
  /** Pick an action suggestion (catalog mode) — host autofills the name. */
  onPickAction: (def: ActionCommandDef) => void;
  /** Pick an arg suggestion (arg mode) — host autofills the value. */
  onPickArg: (suggestion: ActionArgSuggestion) => void;
}

/**
 * `//` action palette — owns the entire `//`-prefix UI surface end-
 * to-end. Suggestion-only: every "commit" path (Tab, Enter, click)
 * routes through the host's autofill rather than dispatching a side
 * effect, which is why there's no `onExecute`.
 *
 * Two render modes (priority order):
 *   - **arg-suggestions**: live autocomplete rows for the committed
 *     action's argument (`// add fi` -> matching external GIS layers).
 *   - **catalog**: registered actions matching the typed prefix.
 */
const ActionPalette: React.FC<ActionPaletteProps> = ({
  dropdownRef,
  suggestions,
  isCatalog,
  isArgMode,
  argSuggestions,
  argActionId,
  argSourceFilter = 'all',
  argSourceCounts,
  isStateCatalogLoading = false,
  onArgSourceFilterChange,
  cursor,
  onCursorChange,
  onPickAction,
  onPickArg,
}) => {
  const view: 'arg' | 'catalog' = isArgMode ? 'arg' : 'catalog';

  let headerLabel: string;
  if (view === 'arg') {
    headerLabel = `Suggestions for //${argActionId ?? ''}`.trim();
  } else if (isCatalog) {
    headerLabel = 'Available actions';
  } else {
    headerLabel = 'Matching actions';
  }

  const headerHint: React.ReactNode =
    view === 'arg' ? (
      <>
        <span className={styles.paletteHeaderHintKey}>Tab</span> to autocomplete
        {' · '}
        <span className={styles.paletteHeaderHintKey}>Enter</span> to run
      </>
    ) : (
      <>
        <span className={styles.paletteHeaderHintKey}>Tab</span> to autocomplete
      </>
    );

  const showSourceFilter =
    view === 'arg' &&
    !!onArgSourceFilterChange &&
    !!argSourceCounts &&
    (argSourceCounts.state > 0 ||
      argSourceCounts.global > 0 ||
      isStateCatalogLoading);

  return (
    <Dropdown ref={dropdownRef} onMouseDown={e => e.preventDefault()}>
      <div className={styles.paletteHeader}>
        <span className={styles.paletteHeaderLabel}>{headerLabel}</span>
        <span className={styles.paletteHeaderHint}>{headerHint}</span>
      </div>
      {showSourceFilter && argSourceCounts && onArgSourceFilterChange && (
        <SourceFilterPills
          filter={argSourceFilter}
          counts={argSourceCounts}
          isStateLoading={isStateCatalogLoading}
          onChange={onArgSourceFilterChange}
        />
      )}

      {view === 'arg'
        ? renderArgRows({
            argSuggestions,
            argActionId,
            argSourceFilter,
            isStateCatalogLoading,
            cursor,
            onCursorChange,
            onPickArg,
          })
        : renderActionRows({
            suggestions,
            cursor,
            onCursorChange,
            onPickAction,
          })}
    </Dropdown>
  );
};

function SourceFilterPills({
  filter,
  counts,
  isStateLoading,
  onChange,
}: {
  filter: ArgSourceFilter;
  counts: { state: number; global: number };
  isStateLoading: boolean;
  onChange: (next: ArgSourceFilter) => void;
}) {
  return (
    <div className={styles.sourceFilterBar}>
      <button
        type="button"
        className={`${styles.sourceFilterPill}${filter === 'state' ? ` ${styles.sourceFilterPillActive}` : ''}`}
        onClick={() => onChange(filter === 'state' ? 'all' : 'state')}
      >
        {isStateLoading ? (
          'Loading state layers...'
        ) : (
          <>
            State
            <span className={styles.sourceFilterCount}>{counts.state}</span>
          </>
        )}
      </button>
      <button
        type="button"
        className={`${styles.sourceFilterPill}${filter === 'global' ? ` ${styles.sourceFilterPillActive}` : ''}`}
        onClick={() => onChange(filter === 'global' ? 'all' : 'global')}
      >
        Global GIS
        <span className={styles.sourceFilterCount}>{counts.global}</span>
      </button>
    </div>
  );
}

function renderActionRows({
  suggestions,
  cursor,
  onCursorChange,
  onPickAction,
}: Pick<
  ActionPaletteProps,
  'suggestions' | 'cursor' | 'onCursorChange' | 'onPickAction'
>) {
  if (suggestions.length === 0) {
    return (
      <EmptyState>
        No matching actions. Type <code>// add</code> to see layer suggestions.
      </EmptyState>
    );
  }
  return suggestions.map((def, idx) => {
    const isActive = cursor === idx;
    return (
      <ResultItem
        key={def.id}
        data-result-index={idx}
        isActive={isActive}
        onClick={() => onPickAction(def)}
        onMouseEnter={() => onCursorChange(idx)}
      >
        <ResultIcon>{iconForAction(def.id)}</ResultIcon>
        <ResultContent>
          <div className={styles.actionSyntax}>
            <span className={styles.actionName}>//{def.id}</span>
            {def.usage && (
              <span className={styles.actionUsage}>{def.usage}</span>
            )}
            <span>{def.title}</span>
          </div>
          <div className={styles.actionDescription}>{def.description}</div>
        </ResultContent>
      </ResultItem>
    );
  });
}

/**
 * Right-aligned chip that mirrors `ActionArgSuggestion.state` so
 * users can see at a glance whether the underlying resource (today:
 * a GIS layer) is already loaded. `available` rows return null —
 * the absence of a badge is itself the "this is new" signal.
 */
function renderStateBadge(
  state: ActionArgSuggestion['state'],
): React.ReactNode {
  if (state === 'active') {
    return (
      <span
        className={`${styles.stateBadge} ${styles.stateBadgeActive}`}
        aria-label="Already on the map"
      >
        On
      </span>
    );
  }
  if (state === 'inactive') {
    return (
      <span
        className={`${styles.stateBadge} ${styles.stateBadgeInactive}`}
        aria-label="Already added — click to turn on"
      >
        Added
      </span>
    );
  }
  return null;
}

const MAX_VISIBLE_TAGS = 4;

function renderMetaPills(
  tags: readonly string[] | undefined,
  stateTag: string | undefined,
): React.ReactNode {
  const normalizedStateTag = stateTag?.trim().toUpperCase();
  const compact = tags
    ?.map(tag => tag.trim())
    .filter(Boolean)
    .slice(0, MAX_VISIBLE_TAGS);
  const safeTags = compact ?? [];
  const hasStateTag = Boolean(normalizedStateTag);
  if (!hasStateTag && safeTags.length === 0) return null;
  const remaining = (tags?.length ?? 0) - safeTags.length;
  return (
    <div className={styles.argMetaTags}>
      {hasStateTag && (
        <span
          className={`${styles.tagPill} ${styles.stateTagPill}`}
          title={`State: ${normalizedStateTag}`}
        >
          State: {normalizedStateTag}
        </span>
      )}
      {safeTags.map(tag => (
        <span key={tag} className={styles.tagPill} title={tag}>
          {tag}
        </span>
      ))}
      {remaining > 0 && (
        <span
          className={`${styles.tagPill} ${styles.tagOverflowPill}`}
          aria-label={`${remaining} more tags`}
          title={`${remaining} more tags`}
        >
          +{remaining}
        </span>
      )}
    </div>
  );
}

function renderArgRows({
  argSuggestions,
  argActionId,
  argSourceFilter,
  isStateCatalogLoading,
  cursor,
  onCursorChange,
  onPickArg,
}: Pick<
  ActionPaletteProps,
  | 'argSuggestions'
  | 'argActionId'
  | 'argSourceFilter'
  | 'isStateCatalogLoading'
  | 'cursor'
  | 'onCursorChange'
  | 'onPickArg'
>) {
  if (argSuggestions.length === 0) {
    if (argSourceFilter === 'state') {
      if (isStateCatalogLoading) {
        return <EmptyState>Loading state layers...</EmptyState>;
      }
      return <EmptyState>No state layers match. Try Global GIS.</EmptyState>;
    }
    if (argSourceFilter === 'global') {
      return <EmptyState>No Global GIS layers match. Try State.</EmptyState>;
    }
    return (
      <EmptyState>No matches. Keep typing or press Esc to cancel.</EmptyState>
    );
  }
  return argSuggestions.map((s, idx) => {
    const isActive = cursor === idx;
    // Background tint mirrors `s.state` so the row reads as "you
    // have this" / "this is on" even before the user scans the
    // trailing badge. Composes with the keyboard-active tint via
    // ResultItem's own `isActive` styling — both can be applied
    // at once (e.g. arrow-keying onto an already-added row).
    const stateClass =
      s.state === 'active'
        ? styles.argRowActive
        : s.state === 'inactive'
          ? styles.argRowInactive
          : '';
    return (
      <ResultItem
        key={s.id}
        data-result-index={idx}
        isActive={isActive}
        onClick={() => onPickArg(s)}
        onMouseEnter={() => onCursorChange(idx)}
        className={stateClass}
      >
        <ResultIcon>{iconForAction(argActionId ?? '')}</ResultIcon>
        <ResultContent>
          <ResultLabel>{s.label}</ResultLabel>
          {s.sublabel && s.source !== 'state' && (
            <ResultSublabel>{s.sublabel}</ResultSublabel>
          )}
          {renderMetaPills(s.tags, s.stateTag)}
          {s.description && (
            <div className={styles.argMetaDescription} title={s.description}>
              {s.description}
            </div>
          )}
        </ResultContent>
        {renderStateBadge(s.state)}
      </ResultItem>
    );
  });
}

export default ActionPalette;
